import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { TreeView, TreeItem } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";



// Default tree structure for vehicles for testing purposes
// const treeDefault = [
//   {
//     id: "cars",
//     name: "Cars",
//     objectType: "vehicle",
//     children: [
//       {
//         id: "toyota",
//         name: "Toyota",
//         objectType: "make",
//         parentId: "cars",
//         parentObjectType: "vehicle",
//         children: [
//           {
//             id: "corolla",
//             name: "Corolla",
//             objectType: "model",
//             parentId: "toyota",
//             parentObjectType: "make",
//           },
//           {
//             id: "camry",
//             name: "Camry",
//             objectType: "model",
//             parentId: "toyota",
//             parentObjectType: "make",
//           },
//         ],
//       },
//       {
//         id: "honda",
//         name: "Honda",
//         objectType: "make",
//         parentId: "cars",
//         children: [
//           {
//             id: "civic",
//             name: "Civic",
//             objectType: "model",
//             parentId: "honda",
//             parentObjectType: "make",
//           },
//           {
//             id: "accord",
//             name: "Accord",
//             objectType: "model",
//             parentId: "honda",
//             parentObjectType: "make",
//           },
//         ],
//       },
//     ],
//   },
//   {
//     id: "trucks",
//     name: "Trucks",
//     objectType: "vehicle",
//     children: [
//       {
//         id: "ford",
//         name: "Ford",
//         objectType: "make",
//         parentId: "trucks",
//         parentObjectType: "vehicle",
//         children: [
//           {
//             id: "f150",
//             name: "F-150",
//             objectType: "model",
//             parentId: "ford",
//             parentObjectType: "make",
//           },
//           {
//             id: "ranger",
//             name: "Ranger",
//             objectType: "model",
//             parentId: "ford",
//             parentObjectType: "make",
//           },
//         ],
//       },
//     ],
//   },
// ];

const getMatchingTree = (nodes, searchTerm, expandedSet = new Set()) => {
  const matched = [];
  for (const node of nodes) {
    const isMatch = node.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (node.children) {
      const childResult = getMatchingTree(
        node.children,
        searchTerm,
        expandedSet
      );
      if (isMatch || childResult.length > 0) {
        expandedSet.add(`${node.id}-${node.objectType}`);
        matched.push({
          ...node,
          children: isMatch ? node.children : childResult,
        });
      }
    } else if (isMatch) {
      matched.push(node);
    }
  }
  return matched;
};

const TreeMenu = ({ labelOpenButton,labelSelectAll, treeArray }) => {



  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  const [treeData, setTreeData] = useState(treeArray || []);
  const [filteredTree, setFilteredTree] = useState([]);
  const [autoExpanded, setAutoExpanded] = useState([]);

  const formReducer = useSelector((state) => state.form);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (formReducer.loading === "succeeded" && treeData.length === 0) {
      const list = [];

      formReducer.data.vehicleTypeWithMakeWithModelList.forEach((item) => {
        // Transform the data structure to match the expected format
        const vehicleType = {
          id: item.id,
          name: item.name,
          objectType: "vehicle",
          children: item.makeWithModelList.map((make) => ({
            id: make.id,
            name: make.name,
            objectType: "make",
            parentId: item.id,
            parentObjectType: "vehicle",
            children: make.modelList.map((model) => ({
              id: model.id,
              name: model.name,
              objectType: "model",
              parentId: make.id,
              parentObjectType: "make",
            })),
          })),
        };
        list.push(vehicleType);
      });

      setTreeData(list);
      setFilteredTree(list);


    }
  }, [formReducer.loading, formReducer.data]);

 





   useMemo(() => {
    if (!searchTerm.trim()) return { filteredTree: treeData, autoExpanded: [] };
    const set = new Set();


    if (searchTerm.trim().length === 1){

      setFilteredTree(treeData);
      setAutoExpanded([]);
      return;
    }
    const filtered = getMatchingTree(treeData, searchTerm, set);
    setFilteredTree(filtered);
    setAutoExpanded(Array.from(set));



   // return { filteredTree: filtered, autoExpanded: Array.from(set) };
  }, [searchTerm, treeData]);


  








  useEffect(() => {
    updateURLQuery(selected);
  }, [selected]);

  const updateURLQuery = (selectedItems) => {
    if (anchorEl) {
      const searchParams = new URLSearchParams(location.search);

      // Get other query parameters if they exist
      const otherParams = {};
      for (let [key, value] of searchParams.entries()) {
        if (key !== labelOpenButton) {
          otherParams[key] = value;
        }
      }

      if (selectedItems.length === 0) {
        // If there are no selected items, remove 'Auctions' parameter
        delete otherParams[labelOpenButton];
      } else {
        otherParams[labelOpenButton] = JSON.stringify(
          selectedItems ? selectedItems : []
        );
      }

      // Set the updated search params back to the URL
      const updatedSearchParams = new URLSearchParams(otherParams);
      navigate({ search: updatedSearchParams.toString() });
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Get the 'Vehicles' parameter from the URL query
    const auctionsParam = searchParams.get(labelOpenButton);

    if (auctionsParam) {
      // Parse the JSON string back into an array of objects
      const auctionsArray = JSON.parse(auctionsParam);

      // Update the 'selected' state with the parsed data
      setSelected(auctionsArray);
    }
  }, [location.search]);

  useMemo(() => {
    if (searchTerm.trim()) {
      setExpanded((prev) => Array.from(new Set([...prev, ...autoExpanded])));
    }
  }, [autoExpanded, searchTerm]);

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };


  const isSelected = (id, objectType) => {
    const checkNode = (nodes) => {
      for (const node of nodes) {
        if (`${node.id}-${node.objectType}`=== id && node.objectType === objectType) return true;
        if (node.children && checkNode(node.children)) return true;
      }
      return false;
    };
    return checkNode(selected);
  };

  const toggleSelection = (node) => {

    const exists = isSelected(`${node.id}-${node.objectType}`, node.objectType);

    const addNode = (tree, nodeToAdd) => {
      const path = [];


      const findPath = (nodes, targetId , targetObjectType) => {
        for (let n of nodes) {
          if (n.id === targetId && n.objectType === targetObjectType) {
            path.push(n);
            return true;
          }
          if (n.children && findPath(n.children, targetId, targetObjectType)) {
            path.push(n);
            return true;
          }
        }
        return false;
      };

      findPath(treeData, node.id,node.objectType);
      path.reverse();

      const buildBranch = (index) => {
        const current = path[index];
        if (index === path.length - 1)
          return {
            id: current.id,
            name: current.name,
            objectType: current.objectType,
          };
        return {
          id: current.id,
          name: current.name,
          objectType: current.objectType,
          children: [buildBranch(index + 1)],
        };
      };

      const merge = (nodes, branch) => {
        const existing = nodes.find((n) => `${n.id}-${n.objectType}` === `${branch.id}-${branch.objectType}`);
        if (!existing) {
          return [...nodes, branch];
        } else {
          return nodes.map((n) => {
            if (`${n.id}-${n.objectType}` === `${branch.id}-${branch.objectType}`) {
              return {
                ...n,
                children: merge(n.children || [], branch.children[0]),
              };
            }
            return n;
          });
        }
      };

      return merge(tree, buildBranch(0));
    };

    const removeNode = (nodes, idToRemove) => {
      const newNodes = [];
      for (let node of nodes) {
        if (`${node.id}-${node.objectType}` === idToRemove) continue;
        let children = node.children
          ? removeNode(node.children, idToRemove)
          : [];
        if (node.children && children.length === 0 && node.children.length > 0)
          continue;
        newNodes.push({
          ...node,
          children: children.length ? children : undefined,
        });
      }
      return newNodes;
    };

    if (exists) {
      setSelected((prev) => removeNode(prev, `${node.id}-${node.objectType}`));
    } else {
      setSelected((prev) => addNode(prev, node));
    }
  };

  const renderTree = (nodes) =>
    nodes.map((node) => (
      <TreeItem
        key={`${node.id}-${node.objectType}`}
        nodeId={`${node.id}-${node.objectType}`}
        label={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={isSelected(`${node.id}-${node.objectType}`, node.objectType)}
              onChange={() => toggleSelection(node)}
            />
            {node.name}
          </Box>
        }
      >
        {node.children && renderTree(node.children)}
      </TreeItem>
    ));


  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {labelOpenButton}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { width: 300, maxHeight: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Box>

        <MenuItem>
          <FormControlLabel
            label={labelSelectAll}
            control={
              <Checkbox
                checked={selected.length === 0}
                onClick={() => setSelected([])}
              />
            }
          />
        </MenuItem>

        <Box sx={{ maxHeight: 300, overflow: "auto", px: 2, pb: 2 }}>
          <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            expanded={expanded}
            onNodeToggle={handleToggle}
          >
            {renderTree(filteredTree)}
          </TreeView>
        </Box>
      </Menu>
    </Box>
  );


  
};


TreeMenu.propTypes = {
  labelOpenButton: PropTypes.string.isRequired,
  labelSelectAll: PropTypes.string.isRequired,
  treeArray: PropTypes.array,
  
};



export default TreeMenu;
